import React, { useState } from "react";
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import mixpanel from 'mixpanel-browser';
import { post } from '../../services/BackendService';
import { trackFormSubmit } from '../../helpers/analytics';
import "./HourlyRentalInquiryComponent.scss";

const HourlyRentalInquiryComponent = ({ transferTitle }) => {
  const [contactForm, setContactForm] = useState({
    full_name: '',
    email_address: '',
    phone_number: '',
    message: 'Hello, I am interested in hourly rental service.',
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const handleContactFormChange = ({target}) => {
    setContactForm(prevForm => ({
      ...prevForm,
      [target.id]: target.value
    }));
  }

  const submitContactForm = (event) => {
    event.preventDefault();
    
    if (event.target.checkValidity()) {
      event.target.classList.remove('was-validated');

      mixpanel.identify(contactForm.email_address);
      mixpanel.people.set({
        $name: contactForm.full_name,
        $email: contactForm.email_address,
      });
      mixpanel.track('Hourly Rental Inquiry Submitted', {
        transfer_page: transferTitle
      });
      
      // Track event with Google Analytics
      trackFormSubmit('hourly_rental_inquiry', 'hourly_rental', {
        transfer_page: transferTitle || 'Transfer Page',
        full_name: contactForm.full_name,
        email_address: contactForm.email_address
      });

      post('/sendEmail', {
        ...contactForm,
        booking_reference: `Hourly Rental Inquiry - ${transferTitle || 'Transfer Page'}`
      })
        .then(response => {
          setSent(true);
          setContactForm({
            full_name: '',
            email_address: '',
            phone_number: '',
            message: '',
          });
        })
        .catch((error) => {
          setError(error.response?.data?.error?.message || 'An error occurred');
        });
    } else {
      event.target.classList.add('was-validated');
    }
  }

  return (
    <section className="hourly-rental-inquiry">
      <div className="container">
        <div className="hourly-rental-card">
          <div className="row">
            <div className="col-lg-6">
              <div className="hourly-rental-info">
                <h2>Hourly Rental Service</h2>
                <p className="lead">Perfect for trips with multiple stops, day trips, and customized journeys</p>
                <div className="hourly-rental-pricing">
                  <span className="price">Starting from 20€/hour</span>
                  <span className="minimum">Minimum 4 hours</span>
                </div>
                <div className="hourly-rental-explanation">
                  <h3>What is Hourly Rental?</h3>
                  <p>With hourly rental, you pay for the driver's time rather than the distance traveled. This is ideal when you need to make multiple stops or want flexibility in your itinerary. The driver stays with you throughout your journey and returns you to your starting point.</p>
                </div>
                <ul className="hourly-rental-features">
                  <li><i className="fa-solid fa-check-circle"></i> Same pick-up and drop-off location</li>
                  <li><i className="fa-solid fa-check-circle"></i> Pay by the hour, not by distance</li>
                  <li><i className="fa-solid fa-check-circle"></i> Driver available for up to 12 hours</li>
                  <li><i className="fa-solid fa-check-circle"></i> Personalized itinerary planning</li>
                  <li><i className="fa-solid fa-check-circle"></i> Travel concierge assistance available</li>
                  <li><i className="fa-solid fa-check-circle"></i> Ideal for sightseeing with multiple stops</li>
                </ul>
                <div className="hourly-rental-whatsapp">
                  <p>Prefer to discuss your needs directly?</p>
                  <a href="https://wa.me/36707177656" target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp">
                    <i className="fa-brands fa-whatsapp"></i> Contact via WhatsApp
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              {!sent ? (
                <form className="hourly-rental-form needs-validation" noValidate onSubmit={submitContactForm}>
                  <h3>Inquire About Hourly Rental</h3>
                  {error && <Alert variant="danger">{error}</Alert>}
                  <div className="row gy-3">
                    <div className="col-md-6 col-12">
                      <div className="form-group">
                        <label htmlFor="full_name">Full Name</label>
                        <Form.Control 
                          type="text" 
                          className="form-control" 
                          id="full_name" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.full_name}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 col-12">
                      <div className="form-group">
                        <label htmlFor="phone_number">Phone Number</label>
                        <Form.Control 
                          type="text" 
                          className="form-control" 
                          id="phone_number" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.phone_number}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="email_address">E-mail Address</label>
                        <Form.Control 
                          type="email" 
                          className="form-control" 
                          id="email_address" 
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.email_address}
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="message">Message</label>
                        <textarea 
                          className="form-control" 
                          id="message" 
                          rows="4"
                          required
                          onChange={handleContactFormChange}
                          value={contactForm.message}
                          placeholder="Please describe your planned journey, including date, number of passengers, and any specific requirements."
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <button 
                        type="submit" 
                        className="btn btn-primary btn-lg w-100" 
                      >
                        Submit Inquiry
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <Alert variant="success" className="text-center success-message">
                  <Alert.Heading>Thank You!</Alert.Heading>
                  <p>
                    We've received your inquiry about our hourly rental service and will contact you shortly.<br />
                    For urgent matters, please contact us via WhatsApp.
                  </p>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HourlyRentalInquiryComponent;
